import { API_URL } from './api.ts';
import { APIError } from './errors.ts';

export { APIError } from './errors.ts';

const buildFormData = (formData: FormData, data: unknown, parentKey?: string) => {
    if (data == null) return;
    if (data instanceof Blob) {
        if (parentKey) formData.append(parentKey, data);
    } else if (data instanceof Date) {
        if (parentKey) formData.append(parentKey, data.toISOString());
    } else if (typeof data === 'object') {
        Object.entries(data).forEach(([key, value]) =>
            buildFormData(formData, value, parentKey ? `${parentKey}[${key}]` : key)
        );
    } else if (parentKey) {
        formData.append(parentKey, String(data));
    }
};

interface TransportOptions {
    headers?: Record<string, string>;
    body?: unknown;
    files?: Record<string, File>;
    signal?: AbortSignal;
}

export const call = async (path: string, method = 'GET', options: TransportOptions = {}): Promise<unknown> => {
    const { files, body: data, ...request } = options;
    let body: BodyInit | undefined;
    const headers = { ...request.headers };
    if (files && Object.keys(files).length > 0) {
        const formData = new FormData();
        buildFormData(formData, data ?? {});
        // set avoids attaching the same top-level file twice.
        Object.entries(files).forEach(([key, file]) => formData.set(key, file));
        body = formData;
    } else if (data !== undefined) {
        body = JSON.stringify(data);
        headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(`${API_URL}${path}`, {
        ...request,
        headers,
        body,
        method,
        credentials: 'include',
    });
    if (!response.ok) {
        const text = await response.text();
        let payload: unknown;
        try {
            payload = JSON.parse(text);
        } catch {
            /* Non-JSON error responses have no field errors. */
        }
        throw new APIError(`Request failed: ${response.statusText} (${response.status})`, payload, response.status);
    }
    if (response.status === 204) return null;
    return response.headers.get('content-type')?.includes('application/json') ? response.json() : response.text();
};

interface Option {
    label: string;
    value: string;
}

type ValueInput = string | [string, string[]];
type OptionOutput = Option | [string, Option[]];

export const valuesToOptions = (values: ValueInput[]): OptionOutput[] => {
    return values.map(
        (value): OptionOutput =>
            typeof value === 'string'
                ? { label: value, value }
                : [value[0], value[1].map((v) => ({ label: v, value: v }))]
    );
};

export const isUuid = (value: string) => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value);
};
